import React, { useState, useMemo } from 'react';

import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import {
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Descriptions,
  Switch,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';

import {
  Campaign,
  AdGroup,
  CampaignAd,
  CAMPAIGN_STATUS_CONFIG,
  PLACEMENT_CONFIG,
  PAYMENT_MODEL_CONFIG,
  GROUPING_LEVEL_CONFIG,
  ANGLE_TYPE_CONFIG,
  AngleType,
} from '../../types';

const { Title, Text } = Typography;

interface CampaignDetailProps {
  campaign: Campaign;
  adGroups: AdGroup[];
  campaignAds: CampaignAd[];
  cabinetName: string;
  offerName: string;
  onBack: () => void;
}

const CampaignDetail: React.FC<CampaignDetailProps> = ({
  campaign,
  adGroups,
  campaignAds,
  cabinetName,
  offerName,
  onBack,
}) => {
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const selectedGroup = useMemo(
    () => adGroups.find((g) => g.id === selectedGroupId) || null,
    [adGroups, selectedGroupId]
  );

  const getAdsForGroup = (groupId: string) => campaignAds.filter((a) => a.adGroupId === groupId);

  const statusCfg = CAMPAIGN_STATUS_CONFIG[campaign.status];

  // --- Group detail ---
  if (selectedGroup) {
    const groupAds = getAdsForGroup(selectedGroup.id);

    const adColumns: ColumnsType<CampaignAd> = [
      {
        title: '#',
        key: 'num',
        width: 40,
        render: (_: unknown, __: CampaignAd, index: number) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {index + 1}
          </Text>
        ),
      },
      {
        title: 'Стиль',
        key: 'angle',
        width: 160,
        render: (_: unknown, record: CampaignAd) => {
          const cfg = ANGLE_TYPE_CONFIG[record.angleType];
          return cfg ? <Tag>{cfg.label}</Tag> : record.angleType;
        },
      },
      {
        title: 'Заголовок',
        key: 'title',
        render: (_: unknown, record: CampaignAd) => (
          <div>
            <div style={{ fontWeight: 600, color: '#1a0dab', fontSize: 14 }}>{record.title}</div>
            {record.title2 && (
              <div style={{ color: '#1a0dab', fontSize: 13 }}>{record.title2}</div>
            )}
          </div>
        ),
      },
      {
        title: 'Текст',
        key: 'text',
        width: 300,
        render: (_: unknown, record: CampaignAd) => (
          <Text style={{ fontSize: 12, color: '#545454' }}>{record.text}</Text>
        ),
      },
      {
        title: 'Статус',
        key: 'status',
        width: 100,
        render: (_: unknown, record: CampaignAd) => {
          const colors: Record<string, string> = {
            active: 'green',
            paused: 'orange',
            draft: 'default',
          };
          return <Tag color={colors[record.status] || 'default'}>{record.status}</Tag>;
        },
      },
    ];

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => setSelectedGroupId(null)}
          />
          <Title level={5} style={{ margin: 0 }}>
            Группа: {selectedGroup.name}
          </Title>
          <Tag>{groupAds.length} объявлений</Tag>
        </div>

        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fafafa', borderRadius: 8 }}>
          <Space size={24} wrap>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Ключевые слова</Text>
              <div style={{ marginTop: 4 }}>
                {selectedGroup.keywords.map((kw, i) => (
                  <Tag key={i} style={{ marginBottom: 4 }}>{kw}</Tag>
                ))}
              </div>
            </div>
            {selectedGroup.suKeywords.length > 0 && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Доп. ключевые (из СЕ)</Text>
                <div style={{ marginTop: 4 }}>
                  {selectedGroup.suKeywords.map((kw, i) => (
                    <Tag key={i} color="blue" style={{ marginBottom: 4 }}>{kw}</Tag>
                  ))}
                </div>
              </div>
            )}
          </Space>
        </div>

        <Table
          rowKey="id"
          columns={adColumns}
          dataSource={groupAds}
          pagination={false}
          size="small"
        />
      </div>
    );
  }

  // --- Campaign overview ---
  const groupColumns: ColumnsType<AdGroup> = [
    {
      title: '#',
      key: 'num',
      width: 40,
      render: (_: unknown, __: AdGroup, index: number) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {index + 1}
        </Text>
      ),
    },
    {
      title: 'Группа',
      key: 'name',
      render: (_: unknown, record: AdGroup) => (
        <a
          onClick={() => setSelectedGroupId(record.id)}
          style={{ fontWeight: 500, color: '#1890ff', cursor: 'pointer' }}
        >
          {record.name}
        </a>
      ),
    },
    {
      title: 'Ключевые слова',
      key: 'keywords',
      width: 250,
      render: (_: unknown, record: AdGroup) => (
        <span style={{ fontSize: 12 }}>
          {record.keywords.slice(0, 3).join(', ')}
          {record.keywords.length > 3 && ` +${record.keywords.length - 3}`}
        </span>
      ),
    },
    {
      title: 'Объявлений',
      key: 'ads',
      width: 100,
      align: 'center',
      render: (_: unknown, record: AdGroup) => <strong>{getAdsForGroup(record.id).length}</strong>,
    },
    {
      title: 'Вкл.',
      key: 'enabled',
      width: 60,
      render: (_: unknown, record: AdGroup) => (
        <Switch checked={record.enabled} size="small" />
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} />
        <Title level={5} style={{ margin: 0 }}>{campaign.name}</Title>
        <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
      </div>

      <Descriptions
        size="small"
        column={3}
        bordered
        style={{ marginBottom: 20 }}
      >
        <Descriptions.Item label="Площадка">Яндекс Директ</Descriptions.Item>
        <Descriptions.Item label="Кабинет">{cabinetName}</Descriptions.Item>
        <Descriptions.Item label="Оффер">{offerName}</Descriptions.Item>
        <Descriptions.Item label="Размещение">{PLACEMENT_CONFIG[campaign.placement]}</Descriptions.Item>
        <Descriptions.Item label="Оплата">{PAYMENT_MODEL_CONFIG[campaign.paymentModel]}</Descriptions.Item>
        <Descriptions.Item label="Бюджет/нед.">{campaign.weeklyBudget.toLocaleString('ru-RU')} ₽</Descriptions.Item>
        <Descriptions.Item label="Цена конверсии">
          {campaign.conversionPriceLimit ? `${campaign.conversionPriceLimit.toLocaleString('ru-RU')} ₽` : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Конверсия">{campaign.conversionName || '—'}</Descriptions.Item>
        <Descriptions.Item label="Группировка">{GROUPING_LEVEL_CONFIG[campaign.settings.groupingLevel]}</Descriptions.Item>
      </Descriptions>

      {campaign.negativeKeywords.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>Минус-фразы:</Text>
          {campaign.negativeKeywords.map((kw, i) => (
            <Tag key={i} style={{ marginBottom: 4 }}>-{kw}</Tag>
          ))}
        </div>
      )}

      <Title level={5} style={{ marginBottom: 12 }}>
        Группы объявлений ({adGroups.length})
      </Title>

      <Table
        rowKey="id"
        columns={groupColumns}
        dataSource={adGroups}
        pagination={false}
        size="small"
      />
    </div>
  );
};

export default CampaignDetail;
